import { useEffect, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { Link } from "wouter";
import { Camera, Plus } from "lucide-react";
import { paths, type Expense } from "@clevis/shared-types";
import { Button, Card, CardTitle, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { draftFromScan, saveExpense, scanExpenseDoc } from "../lib/expenseScan";
import { ExpenseEditor, emptyLine, money, type ExpenseDraft } from "./ExpenseEditor";
import { qbChip } from "../pages/customer/ExpensesPage";

// A job's spending, on the job: supplier invoices for its parts, receipts the
// crew bought on the way, office entries. A tech snaps a receipt here (Claude
// reads it) and it waits for a manager's approval; the office sees every
// expense on the job and opens it to approve or send to QuickBooks.

type Row = Expense & { id: string };

/** What this job carries of an expense: its own lines, plus the charges when it's the only job. */
function onJob(e: Expense, jobId: string): number {
  const lines = e.lines.filter((l) => (l.jobId ?? e.jobId) === jobId).reduce((s, l) => s + l.extendedCents, 0);
  return e.jobIds.length <= 1 ? lines + e.surchargesCents : lines;
}

export function JobExpenses({
  tenantId,
  jobId,
  uid,
  isTech,
}: {
  tenantId: string;
  jobId: string;
  uid: string;
  isTech: boolean;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [draft, setDraft] = useState<ExpenseDraft | null>(null);
  const [docPath, setDocPath] = useState<string | null>(null);
  const [source, setSource] = useState<"receipt" | "manual">("receipt");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Techs read only the receipts they added (rules); the office reads all of the job's.
    const q = isTech
      ? query(collection(db, paths.tenantExpenses(tenantId)), where("createdByUid", "==", uid))
      : query(collection(db, paths.tenantExpenses(tenantId)), where("jobIds", "array-contains", jobId));
    return onSnapshot(
      q,
      (snap) =>
        setRows(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Expense) }))
            .filter((e) => e.jobIds.includes(jobId))
            .sort((a, b) => b.createdAt - a.createdAt),
        ),
      () => setRows([]),
    );
  }, [tenantId, jobId, uid, isTech]);

  async function onFile(file: File) {
    setBusy(true);
    setMessage("");
    try {
      const scan = await scanExpenseDoc(tenantId, file, jobId);
      setDocPath(scan.documentPath);
      setSource("receipt");
      setDraft(draftFromScan(scan, { jobId }));
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function save(d: ExpenseDraft) {
    setBusy(true);
    setMessage("");
    try {
      const res = await saveExpense(tenantId, { ...d, jobId }, { source, documentPath: docPath });
      setDraft(null);
      setDocPath(null);
      setMessage(res.status === "draft" ? `Saved. A manager approves it before it goes to QuickBooks.` : `Expense ${res.number} saved.`);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const total = rows.reduce((s, e) => s + onJob(e, jobId), 0);

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <CardTitle className="mb-0">
          Expenses{rows.length > 0 ? <span className="ml-1 font-normal text-steel">· {money(total)}</span> : null}
        </CardTitle>
        {!draft ? (
          <div className="flex items-center gap-1">
            <Button variant="ghost" className="px-2 py-1 text-xs" busy={busy} onClick={() => fileInput.current?.click()}>
              <Camera size={14} /> Add receipt
            </Button>
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              onClick={() => {
                setSource("manual");
                setDocPath(null);
                setDraft({
                  vendorName: "",
                  invoiceNumber: "",
                  invoiceDate: new Date().toISOString().slice(0, 10),
                  jobId,
                  lines: [emptyLine(jobId)],
                  surcharges: [],
                  note: "",
                });
              }}
            >
              <Plus size={14} /> Type it in
            </Button>
          </div>
        ) : null}
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
            e.target.value = "";
          }}
        />
      </div>
      {busy && !draft ? <p className="mt-2 text-xs text-brandorange-dark">Reading the receipt…</p> : null}
      {message ? <p className="mt-2 text-xs text-ink">{message}</p> : null}

      {draft ? (
        <div className="mt-3 rounded-lg bg-cream p-3">
          <ExpenseEditor
            initial={draft}
            lockJobId={jobId}
            busy={busy}
            saveLabel={isTech ? "Save receipt" : "Save expense"}
            onSave={(d) => void save(d)}
            onCancel={() => setDraft(null)}
          />
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="mt-2 divide-y divide-steel/10">
          {rows.map((e) => {
            const chip = qbChip(e);
            const label = (
              <>
                <span className="min-w-0 flex-1 truncate text-sm text-ink">
                  #{e.number} · {e.vendorName}
                </span>
                {e.status === "draft" ? <span className="rounded-full bg-steel/15 px-2 py-0.5 text-[10px] text-steel">needs approval</span> : null}
                {chip && !isTech ? <span className={cn("rounded-full px-2 py-0.5 text-[10px]", chip.style)}>{chip.label}</span> : null}
                <span className="w-20 shrink-0 text-right text-sm text-ink">{money(onJob(e, jobId))}</span>
              </>
            );
            return isTech ? (
              <div key={e.id} className="flex items-center gap-2 py-1.5">
                {label}
              </div>
            ) : (
              <Link key={e.id} href={`/expenses/${e.id}`} className="flex items-center gap-2 py-1.5 hover:bg-cream/60">
                {label}
              </Link>
            );
          })}
        </div>
      ) : !draft ? (
        <p className="mt-1 text-xs text-steel">Parts and materials bought for this job show here.</p>
      ) : null}
    </Card>
  );
}
